import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/components/MetricCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useExpansionSummary, useExpansionCustomers, useExpansionCustomerDetail } from '../hooks';
import type { ExpansionCategory, ExpansionUpsellSignal } from '../types';
import {
  RefreshCw, TrendingUp, AlertTriangle, Sparkles, Building2,
  Search, ChevronRight, X, Clock, Users, Activity, Gauge, HelpCircle, Info
} from 'lucide-react';
import { cn } from '@/lib/utils';

type FilterCategory = 'all' | ExpansionCategory;
type SortField = 'expansion_score' | 'risk_score' | 'name' | 'tenure' | 'health_score';

const PAGE_SIZE = 25;

// Table header with tooltip
function TableHeaderWithTooltip({
  label,
  tooltip,
  sortField,
  currentSort,
  sortOrder,
  onSort,
  className,
}: {
  label: string;
  tooltip: string;
  sortField?: SortField;
  currentSort?: SortField;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: SortField) => void;
  className?: string;
}) {
  const isSortable = sortField && onSort;
  const isActive = sortField === currentSort;

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <th
      className={cn(
        'text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3',
        isSortable && 'cursor-pointer hover:bg-gray-100',
        className
      )}
      onClick={isSortable ? () => onSort(sortField) : undefined}
    >
      <div className="flex items-center justify-center gap-1">
        <span>{label}</span>
        {isActive && <span className="ml-0.5">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleInfoClick}
                className="inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm"
                aria-label={`Info about ${label}`}
              >
                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs font-normal normal-case">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </th>
  );
}

// How it works dialog content
function HowItWorksDialog() {
  const { t } = useTranslation('intelligence');

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
          <HelpCircle className="h-4 w-4" />
          <span className="hidden sm:inline">{t('expansion.howItWorks.button')}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('expansion.howItWorks.title')}</DialogTitle>
          <DialogDescription>
            {t('expansion.howItWorks.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Expansion Score */}
          <div className="space-y-3">
            <h3 className="font-semibold text-green-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t('expansion.howItWorks.expansionScore.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('expansion.howItWorks.expansionScore.description')}
            </p>
            <div className="bg-green-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between"><span>{t('expansion.factors.utilization')}</span><span className="font-medium">25%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.expansionScore.utilization')}</p>
              <div className="flex justify-between"><span>{t('expansion.factors.supportHealth')}</span><span className="font-medium">25%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.expansionScore.supportHealth')}</p>
              <div className="flex justify-between"><span>{t('expansion.factors.engagement')}</span><span className="font-medium">20%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.expansionScore.engagement')}</p>
              <div className="flex justify-between"><span>{t('expansion.factors.tenure')}</span><span className="font-medium">15%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.expansionScore.tenure')}</p>
              <div className="flex justify-between"><span>{t('expansion.factors.growthPotential')}</span><span className="font-medium">15%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.expansionScore.growthPotential')}</p>
            </div>
          </div>

          {/* Risk Score */}
          <div className="space-y-3">
            <h3 className="font-semibold text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t('expansion.howItWorks.riskScore.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('expansion.howItWorks.riskScore.description')}
            </p>
            <div className="bg-red-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between"><span>{t('expansion.factors.activityDecline')}</span><span className="font-medium">30%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.riskScore.activityDecline')}</p>
              <div className="flex justify-between"><span>{t('expansion.factors.alertBurden')}</span><span className="font-medium">25%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.riskScore.alertBurden')}</p>
              <div className="flex justify-between"><span>{t('expansion.factors.supportEscalation')}</span><span className="font-medium">25%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.riskScore.supportEscalation')}</p>
              <div className="flex justify-between"><span>{t('expansion.factors.engagementDecline')}</span><span className="font-medium">20%</span></div>
              <p className="text-xs text-muted-foreground pl-2">{t('expansion.howItWorks.riskScore.engagementDecline')}</p>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <h3 className="font-semibold">{t('expansion.howItWorks.categories.title')}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <div className="font-medium text-green-800">{t('expansion.categories.expansionTarget')}</div>
                <div className="text-xs text-muted-foreground mt-1">{t('expansion.howItWorks.categories.expansionTarget')}</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                <div className="font-medium text-red-800">{t('expansion.categories.atRisk')}</div>
                <div className="text-xs text-muted-foreground mt-1">{t('expansion.howItWorks.categories.atRisk')}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <div className="font-medium text-purple-800">{t('expansion.categories.upsell')}</div>
                <div className="text-xs text-muted-foreground mt-1">{t('expansion.howItWorks.categories.upsell')}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="font-medium text-gray-800">{t('expansion.categories.stable')}</div>
                <div className="text-xs text-muted-foreground mt-1">{t('expansion.howItWorks.categories.stable')}</div>
              </div>
            </div>
          </div>

          {/* Upsell Signals */}
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              {t('expansion.howItWorks.signals.title')}
            </h3>
            <div className="text-sm space-y-2">
              <div className="flex items-start gap-2">
                <Gauge className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div><span className="font-medium">{t('expansion.signals.sensorLimit')}:</span> {t('expansion.howItWorks.signals.sensorCapacity')}</div>
              </div>
              <div className="flex items-start gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div><span className="font-medium">{t('expansion.signals.newSite')}:</span> {t('expansion.howItWorks.signals.newSites')}</div>
              </div>
              <div className="flex items-start gap-2">
                <Activity className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div><span className="font-medium">{t('expansion.signals.calibrationExpansion')}:</span> {t('expansion.howItWorks.signals.calibrationExpansion')}</div>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div><span className="font-medium">{t('expansion.signals.featureGap')}:</span> {t('expansion.howItWorks.signals.featureGap')}</div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScoreBar({ score, colorClass }: { score: number; colorClass: string }) {
  return (
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden w-full">
      <div
        className={cn('h-full rounded-full transition-all', colorClass)}
        style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
      />
    </div>
  );
}

function UpsellSignalBadges({ signals }: { signals: ExpansionUpsellSignal[] }) {
  if (!signals || signals.length === 0) return null;

  const signalIcons: Record<string, React.ReactNode> = {
    sensor_limit: <Gauge className="h-3 w-3" />,
    new_site: <Building2 className="h-3 w-3" />,
    calibration_expansion: <Activity className="h-3 w-3" />,
    feature_gap: <Sparkles className="h-3 w-3" />,
  };

  const signalColors: Record<string, string> = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  return (
    <div className="flex gap-1 flex-wrap">
      {signals.slice(0, 3).map((signal, idx) => (
        <Badge
          key={idx}
          variant="outline"
          className={cn('text-xs py-0 px-1.5', signalColors[signal.priority])}
          title={signal.description}
        >
          {signalIcons[signal.type] || <Sparkles className="h-3 w-3" />}
          <span className="ml-1 hidden sm:inline">{signal.label}</span>
        </Badge>
      ))}
      {signals.length > 3 && (
        <Badge variant="outline" className="text-xs py-0 px-1.5">
          +{signals.length - 3}
        </Badge>
      )}
    </div>
  );
}

function CategoryBadge({ category }: { category: ExpansionCategory }) {
  const categoryStyles: Record<ExpansionCategory, string> = {
    expansion_target: 'bg-green-100 text-green-800 border-green-200',
    at_risk: 'bg-red-100 text-red-800 border-red-200',
    upsell: 'bg-purple-100 text-purple-800 border-purple-200',
    stable: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  const categoryLabels: Record<ExpansionCategory, string> = {
    expansion_target: 'Expansion',
    at_risk: 'At Risk',
    upsell: 'Upsell',
    stable: 'Stable',
  };

  return (
    <Badge variant="outline" className={cn('text-xs', categoryStyles[category])}>
      {categoryLabels[category]}
    </Badge>
  );
}

export default function ExpansionIntelligencePage() {
  const { t } = useTranslation('intelligence');
  const [page, setPage] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState<FilterCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('expansion_score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const { data: summaryData, isLoading: isSummaryLoading, refetch: refetchSummary } = useExpansionSummary();

  const { data: customersData, isLoading: isCustomersLoading, isFetching, refetch: refetchCustomers } = useExpansionCustomers({
    page,
    pageSize: PAGE_SIZE,
    category: categoryFilter === 'all' ? undefined : categoryFilter,
    search: searchQuery,
    sort: sortField,
    order: sortOrder,
  });

  const { data: customerDetail, isLoading: isDetailLoading } = useExpansionCustomerDetail(
    selectedCustomerId || '',
    false
  );

  const customers = customersData?.customers || [];
  const total = customersData?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  const handleRefresh = useCallback(() => {
    refetchSummary();
    refetchCustomers();
  }, [refetchSummary, refetchCustomers]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setSelectedCustomerId(null);
  }, []);

  const handleCategoryFilterChange = useCallback((category: FilterCategory) => {
    setCategoryFilter(category);
    setPage(0);
    setSelectedCustomerId(null);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
    setSelectedCustomerId(null);
  }, []);

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
    setPage(0);
  }, [sortField]);

  const getScoreColor = (score: number, isRisk = false) => {
    if (isRisk) {
      if (score >= 60) return 'bg-red-500';
      if (score >= 30) return 'bg-yellow-500';
      return 'bg-green-500';
    }
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const summary = summaryData || {
    total_organisations: 0,
    expansion_targets: 0,
    at_risk: 0,
    upsell_opportunities: 0,
    stable: 0,
    avg_expansion_score: 0,
    avg_risk_score: 0,
  };

  const isLoading = isSummaryLoading || isCustomersLoading;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('expansion.title', 'Customer Expansion Intelligence')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('expansion.subtitle', 'Identify growth opportunities and retention risks')}</p>
        </div>
        <div className="flex items-center gap-2">
          <HowItWorksDialog />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            {t('common:refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('expansion.cards.expansionTargets', 'Expansion Targets')}
          value={summary.expansion_targets}
          icon={<TrendingUp className="h-5 w-5" />}
          color="green"
          loading={isSummaryLoading}
          tooltip={t('expansion.tooltips.expansionTargets')}
        />
        <MetricCard
          title={t('expansion.cards.atRisk', 'At Risk')}
          value={summary.at_risk}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="red"
          loading={isSummaryLoading}
          tooltip={t('expansion.tooltips.atRisk')}
        />
        <MetricCard
          title={t('expansion.cards.upsellOpportunities', 'Upsell Signals')}
          value={summary.upsell_opportunities}
          icon={<Sparkles className="h-5 w-5" />}
          color="purple"
          loading={isSummaryLoading}
          tooltip={t('expansion.tooltips.upsellOpportunities')}
        />
        <MetricCard
          title={t('expansion.cards.avgScore', 'Avg. Expansion Score')}
          value={summary.avg_expansion_score?.toFixed(0) || '--'}
          icon={<Gauge className="h-5 w-5" />}
          color="blue"
          loading={isSummaryLoading}
          tooltip={t('expansion.tooltips.avgExpansionScore')}
        />
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2 flex-wrap">
          {(['all', 'expansion_target', 'at_risk', 'upsell', 'stable'] as FilterCategory[]).map((category) => (
            <Button
              key={category}
              variant={categoryFilter === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleCategoryFilterChange(category)}
            >
              {category === 'all' ? t('expansion.filters.all', 'All') :
               category === 'expansion_target' ? t('expansion.filters.expansionTarget', 'Expansion') :
               category === 'at_risk' ? t('expansion.filters.atRisk', 'At Risk') :
               category === 'upsell' ? t('expansion.filters.upsell', 'Upsell') :
               t('expansion.filters.stable', 'Stable')}
            </Button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('expansion.search', 'Search accounts...')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>
      </div>

      {/* Customer Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th
                    className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    {t('expansion.table.customer', 'Customer')}
                    {sortField === 'name' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <TableHeaderWithTooltip
                    label={t('expansion.table.expansionScore', 'Expansion')}
                    tooltip={t('expansion.tooltips.expansionScore')}
                    sortField="expansion_score"
                    currentSort={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <TableHeaderWithTooltip
                    label={t('expansion.table.riskScore', 'Risk')}
                    tooltip={t('expansion.tooltips.riskScore')}
                    sortField="risk_score"
                    currentSort={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <TableHeaderWithTooltip
                    label={t('expansion.table.tenure', 'Tenure')}
                    tooltip={t('expansion.tooltips.tenure')}
                    sortField="tenure"
                    currentSort={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    {t('expansion.table.signals', 'Signals')}
                  </th>
                  <TableHeaderWithTooltip
                    label={t('expansion.table.health', 'Health')}
                    tooltip={t('expansion.tooltips.healthScore')}
                    sortField="health_score"
                    currentSort={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="text-center"
                  />
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                    {t('expansion.table.category', 'Category')}
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                    {t('expansion.table.action', 'Action')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      {t('expansion.noResults', 'No customers found')}
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr
                      key={customer._id}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedCustomerId(customer._id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{customer.name}</span>
                        {customer.org_num && (
                          <span className="ml-2 text-xs text-gray-500">#{customer.org_num}</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm w-8 text-right">{customer.expansion_score?.toFixed(0)}</span>
                          <div className="flex-1 min-w-[60px]">
                            <ScoreBar score={customer.expansion_score} colorClass={getScoreColor(customer.expansion_score)} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm w-8 text-right">{customer.risk_score?.toFixed(0)}</span>
                          <div className="flex-1 min-w-[60px]">
                            <ScoreBar score={customer.risk_score} colorClass={getScoreColor(customer.risk_score, true)} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1 text-sm text-gray-600">
                          <Clock className="h-3 w-3" />
                          {customer.tenure_months} mo
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <UpsellSignalBadges signals={customer.upsell_signals} />
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Badge
                          variant="outline"
                          className={cn(
                            'font-semibold',
                            (customer.health_score || 0) >= 80 && 'bg-green-100 text-green-800 border-green-200',
                            (customer.health_score || 0) >= 50 && (customer.health_score || 0) < 80 && 'bg-yellow-100 text-yellow-800 border-yellow-200',
                            (customer.health_score || 0) < 50 && 'bg-red-100 text-red-800 border-red-200',
                          )}
                        >
                          {customer.health_score?.toFixed(0) || '--'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <CategoryBadge category={customer.category} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <ChevronRight className="h-5 w-5 text-gray-400 inline" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t">
              <p className="text-sm text-gray-500">
                Showing {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, total)} of {total} customers
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0 || isFetching}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages - 1 || isFetching}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Slide Panel */}
      {selectedCustomerId && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setSelectedCustomerId(null)}
          />
          <div className="fixed right-0 top-0 h-full w-[450px] bg-white shadow-xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {isDetailLoading ? <Skeleton className="h-6 w-40" /> : customerDetail?.name}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setSelectedCustomerId(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {isDetailLoading ? (
              <div className="p-6 space-y-6">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            ) : customerDetail ? (
              <div className="p-6 space-y-6">
                {/* Score Overview */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-3xl font-bold text-green-700">
                      {customerDetail.expansion_score?.toFixed(0)}
                    </div>
                    <div className="text-sm text-green-600 mt-1">Expansion Score</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-3xl font-bold text-red-700">
                      {customerDetail.risk_score?.toFixed(0)}
                    </div>
                    <div className="text-sm text-red-600 mt-1">Risk Score</div>
                  </div>
                </div>

                {/* Category */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Category:</span>
                  <CategoryBadge category={customerDetail.category} />
                </div>

                {/* Expansion Factors */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Expansion Factors</h3>
                  {[
                    { label: 'Utilization', value: customerDetail.expansion_factors?.utilization?.score, weight: 25 },
                    { label: 'Support Health', value: customerDetail.expansion_factors?.support_health?.score, weight: 25 },
                    { label: 'Engagement', value: customerDetail.expansion_factors?.engagement?.score, weight: 20 },
                    { label: 'Tenure', value: customerDetail.expansion_factors?.tenure?.score, weight: 15 },
                    { label: 'Growth Potential', value: customerDetail.expansion_factors?.growth_potential?.score, weight: 15 },
                  ].map((factor) => (
                    <div key={factor.label} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{factor.label} ({factor.weight}%)</span>
                        <span className="font-medium">{factor.value?.toFixed(1) ?? '--'}</span>
                      </div>
                      <ScoreBar score={factor.value || 0} colorClass={getScoreColor(factor.value || 0)} />
                    </div>
                  ))}
                </div>

                {/* Risk Factors */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Risk Factors</h3>
                  {[
                    { label: 'Activity Decline', value: customerDetail.risk_factors?.activity_decline?.score, weight: 30 },
                    { label: 'Alert Burden', value: customerDetail.risk_factors?.alert_burden?.score, weight: 25 },
                    { label: 'Support Escalation', value: customerDetail.risk_factors?.support_escalation?.score, weight: 25 },
                    { label: 'Engagement Decline', value: customerDetail.risk_factors?.engagement_decline?.score, weight: 20 },
                  ].map((factor) => (
                    <div key={factor.label} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{factor.label} ({factor.weight}%)</span>
                        <span className="font-medium">{factor.value?.toFixed(1) ?? '--'}</span>
                      </div>
                      <ScoreBar score={factor.value || 0} colorClass={getScoreColor(factor.value || 0, true)} />
                    </div>
                  ))}
                </div>

                {/* Upsell Signals */}
                {customerDetail.upsell_signals && customerDetail.upsell_signals.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-medium text-gray-900">Upsell Signals</h3>
                    <div className="space-y-2">
                      {customerDetail.upsell_signals.map((signal, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className={cn(
                            'p-1.5 rounded',
                            signal.priority === 'high' && 'bg-red-100 text-red-600',
                            signal.priority === 'medium' && 'bg-yellow-100 text-yellow-600',
                            signal.priority === 'low' && 'bg-blue-100 text-blue-600',
                          )}>
                            <Sparkles className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">{signal.label}</div>
                            <div className="text-xs text-gray-500">{signal.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {customerDetail.recommendations && customerDetail.recommendations.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-medium text-gray-900">Recommendations</h3>
                    <div className="space-y-2">
                      {customerDetail.recommendations.map((rec, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-xs',
                                rec.type === 'expansion' && 'bg-green-100 text-green-700',
                                rec.type === 'retention' && 'bg-red-100 text-red-700',
                                rec.type === 'upsell' && 'bg-purple-100 text-purple-700',
                              )}
                            >
                              {rec.type}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-xs',
                                rec.priority === 'high' && 'bg-red-50 text-red-600',
                                rec.priority === 'medium' && 'bg-yellow-50 text-yellow-600',
                                rec.priority === 'low' && 'bg-gray-50 text-gray-600',
                              )}
                            >
                              {rec.priority}
                            </Badge>
                          </div>
                          <div className="font-medium text-sm">{rec.title}</div>
                          <div className="text-xs text-gray-500 mt-1">{rec.description}</div>
                          {rec.action && (
                            <div className="text-xs text-blue-600 mt-2 font-medium">{rec.action}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-gray-500">
                      <Users className="h-4 w-4" />
                      <span className="text-xs">Active Sensors</span>
                    </div>
                    <div className="text-lg font-semibold mt-1">
                      {customerDetail.expansion_factors?.utilization?.active_sensors || 0}
                      <span className="text-gray-400 text-sm font-normal">
                        /{customerDetail.expansion_factors?.utilization?.total_sensors || 0}
                      </span>
                    </div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-gray-500">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs">Tenure</span>
                    </div>
                    <div className="text-lg font-semibold mt-1">
                      {customerDetail.expansion_factors?.tenure?.tenure_months || 0} mo
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
